'use client';

import { Button } from '@/components/ui/button';
import { SFIcon } from '@/components/ui/sf-icon';
import { track } from '@/lib/analytics/track';
import { sfIcons } from '@/lib/sf-icons';
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import * as React from 'react';

type CTA = { label: string; onClick: () => void; 'data-testid'?: string };

type EmptyStateVariant =
  | 'no-results'    // Search/filter returned empty
  | 'no-data'       // Table/list has no data
  | 'network-error' // Network/API failure
  | 'no-access'     // Permission denied
  | 'error'         // Generic error state
  | 'onboarding'    // Onboarding success/welcome (zen state)
  | 'premium'       // Premium feature access (premium state)
  | 'default';      // Generic empty state

const VARIANT_CONFIG = {
  'no-results': {
    icon: sfIcons.search,
    iconColor: 'text-muted-foreground',
    iconBg: 'bg-muted',
    defaultTitle: 'No Results Found',
    defaultDescription: 'Adjust your search or filters to find what you\'re looking for.'
  },
  'no-data': {
    icon: sfIcons.noData, // Inbox icon per PRD-ADM-UX-2026
    iconColor: 'text-muted-foreground',
    iconBg: 'bg-muted',
    defaultTitle: 'No Data Yet',
    defaultDescription: 'Get started by creating your first item.'
  },
  'network-error': {
    icon: sfIcons.wifiOff, // Use wifiOff for network errors
    iconColor: 'text-destructive',
    iconBg: 'bg-destructive/10',
    defaultTitle: 'Connection Issue',
    defaultDescription: "We can't load the data right now. Check your connection and try again."
  },
  'no-access': {
    icon: sfIcons.lock,
    iconColor: 'text-warning',
    iconBg: 'bg-warning/10',
    defaultTitle: 'Access Restricted',
    defaultDescription: 'You don\'t have permission to view this content.'
  },
  'error': {
    icon: sfIcons.error, // XCircle icon
    iconColor: 'text-destructive',
    iconBg: 'bg-destructive/10',
    defaultTitle: 'Request Unavailable',
    defaultDescription: "We couldn't load this content. Try again in a moment."
  },
  'default': {
    icon: sfIcons.noData, // Use Inbox for default empty state
    iconColor: 'text-muted-foreground',
    iconBg: 'bg-muted',
    defaultTitle: 'Nothing Here',
    defaultDescription: 'This area is currently empty.'
  },
  // Zen State: Cloud Dancer (Canon v1.4.5)
  'onboarding': {
    icon: sfIcons.rocket,
    iconColor: semanticTokens.zen.fg,
    iconBg: semanticTokens.zen.bg,
    defaultTitle: 'Welcome to Supabank',
    defaultDescription: 'Your journey to seamless crypto-to-fiat conversion begins here.'
  },
  // Premium State: Black Luxury (Canon v1.4.5)
  'premium': {
    icon: sfIcons.star,
    iconColor: semanticTokens.premium.fg,
    iconBg: semanticTokens.premium.bg,
    defaultTitle: 'Premium Feature',
    defaultDescription: 'Upgrade your account to access exclusive features.'
  }
} as const;

export function EmptyState({
  title,
  description,
  illustration,
  cta,
  secondaryCta,
  variant = 'default',
  className,
  'data-testid': testId = 'empty-state',
}: {
  title?: string;
  description?: string;
  illustration?: React.ReactNode;
  cta?: CTA;
  secondaryCta?: CTA;
  variant?: EmptyStateVariant;
  className?: string;
  'data-testid'?: string;
}) {
  const config = VARIANT_CONFIG[variant];
  const finalTitle = title || config.defaultTitle;
  const finalDescription = description || config.defaultDescription;
  React.useEffect(() => {
    track('empty_state_view', {
      variant,
      title: finalTitle,
      hasCtaActions: Boolean(cta || secondaryCta)
    });
  }, [variant, finalTitle, cta, secondaryCta]);

  return (
    <section
      className={cn(
        'flex flex-col items-center justify-center text-center',
        semanticTokens.radius.xl,
        semanticTokens.border.default,
        semanticTokens.bg.surface,
        semanticTokens.spacing.xl,
        'min-h-[320px] gap-4',
        semanticTokens.motion.standard,
        className
      )}
      aria-live="polite"
      data-testid={testId}
      data-variant={variant}
    >
      {illustration || (
        <div className="mb-4" aria-hidden="true">
          <div className={cn(
            'w-20 h-20 rounded-full flex items-center justify-center', // Revolut-style: более крупная иконка
            semanticTokens.radius.full,
            config.iconBg
          )}>
            <SFIcon
              icon={config.icon}
              size="xl"
              variant="default"
              className={config.iconColor}
            />
          </div>
        </div>
      )}

      <h3 className={cn(semanticTokens.typography.h3, 'font-semibold mb-2')}>
        {finalTitle}
      </h3>
      {finalDescription && (
        <p className={cn(semanticTokens.typography.body, semanticTokens.fg.muted, 'max-w-md')}>
          {finalDescription}
        </p>
      )}

      {(cta || secondaryCta) && (
        <div className={cn('mt-2 flex items-center gap-3')}>
          {cta && (
            <Button
              onClick={() => {
                track('empty_state_cta_click', {
                  label: cta.label,
                  variant,
                  title: finalTitle
                });
                cta.onClick();
              }}
              className={cn(
                semanticTokens.motion.standard,
                'focus-visible:ring-2 focus-visible:ring-blue-500'
              )}
              data-testid={cta['data-testid'] ?? 'empty-cta-primary'}
            >
              {cta.label}
            </Button>
          )}
          {secondaryCta && (
            <Button
              variant="outline"
              onClick={() => {
                track('empty_state_cta_click', {
                  label: secondaryCta.label,
                  variant,
                  title: finalTitle
                });
                secondaryCta.onClick();
              }}
              className={cn(
                semanticTokens.motion.standard,
                semanticTokens.focus.ring
              )}
              data-testid={secondaryCta['data-testid'] ?? 'empty-cta-secondary'}
            >
              {secondaryCta.label}
            </Button>
          )}
        </div>
      )}
    </section>
  );
}